/**
 * usePageShortcuts — Per-view keyboard shortcuts for list views.
 *
 * Provides: N for new, Enter to open selected row, J/K or ArrowUp/Down for
 * row navigation, E to edit, Delete/Backspace to delete, / to focus search.
 *
 * Usage in a list view:
 *   const { selectedIndex } = usePageShortcuts({
 *     items: paginatedItems,
 *     onNew: () => { showCreateModal.value = true },
 *     onOpen: (item) => router.push(`/invoices/${item.id}`),
 *     onEdit: (item) => openEditModal(item),
 *     onDelete: (item) => confirmDelete(item),
 *     searchRef: searchInputRef,
 *   })
 *
 * Pass `selectedIndex` to IntercomTable's `:selected-index` prop.
 */

import { ref, onMounted, onUnmounted, watch } from 'vue'

const INPUT_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT'])

/**
 * @param {Object} options
 * @param {import('vue').Ref<Array>} options.items - Reactive list of displayed items
 * @param {Function} [options.onNew] - Called when N is pressed
 * @param {Function} [options.onOpen] - Called with selected item when Enter is pressed
 * @param {Function} [options.onEdit] - Called with selected item when E is pressed
 * @param {Function} [options.onDelete] - Called with selected item when Delete is pressed
 * @param {import('vue').Ref} [options.searchRef] - Ref to search input element (for / shortcut)
 * @param {Function} [options.canEdit] - Optional guard: (item) => boolean. Defaults to () => true
 * @param {Function} [options.canDelete] - Optional guard: (item) => boolean. Defaults to () => true
 */
export function usePageShortcuts(options = {}) {
  const {
    items,
    onNew,
    onOpen,
    onEdit,
    onDelete,
    searchRef,
    canEdit = () => true,
    canDelete = () => true,
  } = options

  const selectedIndex = ref(-1)

  const isInInput = () => {
    const el = document.activeElement
    if (!el) return false
    if (INPUT_TAGS.has(el.tagName)) return true
    if (el.isContentEditable) return true
    return false
  }

  const isModalOpen = () => {
    return !!document.querySelector('.intercom-modal, .intercom-modal-overlay')
  }

  const isPOSActive = () => {
    return !!document.querySelector('.pos-view')
  }

  const getSelectedItem = () => {
    const list = items?.value
    if (!list || selectedIndex.value < 0 || selectedIndex.value >= list.length) return null
    return list[selectedIndex.value]
  }

  const moveSelection = (delta) => {
    const list = items?.value
    if (!list || list.length === 0) return

    const newIndex = selectedIndex.value + delta
    if (newIndex >= 0 && newIndex < list.length) {
      selectedIndex.value = newIndex

      // Scroll the selected row into view
      requestAnimationFrame(() => {
        const rows = document.querySelectorAll('.intercom-table tbody tr')
        if (rows[selectedIndex.value]) {
          rows[selectedIndex.value].scrollIntoView({ block: 'nearest', behavior: 'smooth' })
        }
      })
    }
  }

  const handleKeydown = (e) => {
    // Never interfere with POS
    if (isPOSActive()) return

    // Never interfere when modal is open
    if (isModalOpen()) return

    // Never interfere with modifier combos
    if (e.ctrlKey || e.metaKey || e.altKey) return

    const key = e.key

    // --- Shortcuts that work even in inputs ---

    // Escape: blur input to re-enable shortcuts
    if (key === 'Escape') {
      if (isInInput()) {
        document.activeElement.blur()
        e.preventDefault()
      }
      return
    }

    // --- Shortcuts suppressed when in input ---
    if (isInInput()) return

    // / : focus search
    if (key === '/') {
      const el = searchRef?.value
      if (el) {
        e.preventDefault()
        // Handle both native elements and component refs
        if (typeof el.focus === 'function') {
          el.focus()
        } else if (el.$el) {
          const input = el.$el.querySelector('input')
          input?.focus()
          input?.select()
        }
      }
      return
    }

    // N: new item
    if (key === 'n' || key === 'N') {
      if (onNew) {
        e.preventDefault()
        onNew()
      }
      return
    }

    // J or ArrowDown: next row
    if (key === 'j' || key === 'ArrowDown') {
      e.preventDefault()
      moveSelection(1)
      return
    }

    // K or ArrowUp: previous row
    if (key === 'k' || key === 'ArrowUp') {
      e.preventDefault()
      moveSelection(-1)
      return
    }

    // Enter: open selected row
    if (key === 'Enter') {
      const item = getSelectedItem()
      if (item && onOpen) {
        e.preventDefault()
        onOpen(item)
      }
      return
    }

    // E: edit selected row
    if (key === 'e' || key === 'E') {
      const item = getSelectedItem()
      if (item && onEdit && canEdit(item)) {
        e.preventDefault()
        onEdit(item)
      }
      return
    }

    // Delete or Backspace: delete selected row
    if (key === 'Delete' || key === 'Backspace') {
      const item = getSelectedItem()
      if (item && onDelete && canDelete(item)) {
        e.preventDefault()
        onDelete(item)
      }
      return
    }
  }

  // Reset selection when items change (pagination, search)
  if (items) {
    watch(items, () => {
      selectedIndex.value = -1
    })
  }

  onMounted(() => {
    document.addEventListener('keydown', handleKeydown)
  })

  onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown)
  })

  return {
    selectedIndex,
  }
}
